import { crewInComponentOf } from "./crew";
import { jitterAngle } from "./physics/config";
import {
  cardinalVec,
  moduleWorldCenter,
  rotateCardinal,
  shipCellSize,
} from "./ship";
import type {
  Ship,
  ShipModule,
  World,
} from "./types";

const ATTRACT_RANGE_M = 600;
const ATTRACT_FORCE_N = 1_200;
const ATTRACT_MAX_CLOSING_M_S = 18;
export const LOCK_RANGE_M = 9;
const LOCK_AXIAL_COS = 0.97;
const LOCK_FACING_COS = -0.97;
const BOARD_CONTROL_RATIO = 1.75;
const TRADE_FRIENDLINESS_THRESHOLD = 0;

const FLASH_HZ = 4;

export function isDockingModule(m: ShipModule): boolean {
  return m.def.category === "docking";
}

interface DockingPeer {
  ship: Ship;
  module: ShipModule;
  moduleIdx: number;
  distance: number;
}

function collarDockDir(ship: Ship, m: ShipModule): { x: number; y: number } {
  const shipDir = cardinalVec(rotateCardinal(0, m.facing));
  const rot = jitterAngle(ship.body.rot);
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  return {
    x: shipDir.x * c - shipDir.y * s,
    y: shipDir.x * s + shipDir.y * c,
  };
}

// Per-pass cache of unbonded docking collars (with world centers) so the
// in-tick attract/flash loops don't rescan all ships × modules per collar.
// Rebuilt immediately before each loop because the lock loop in between mutates
// bonding state and ship positions; positions are otherwise stable within a
// loop. The public findNearestOtherCollar stays brute-force for UI callers.
interface CollarCand {
  ship: Ship;
  module: ShipModule;
  idx: number;
  cx: number;
  cy: number;
}
const _collarCands: CollarCand[] = [];

function rebuildCollarCands(world: World): void {
  _collarCands.length = 0;
  for (const ship of world.ships.values()) {
    if (ship.isAsteroid) continue;
    for (let i = 0; i < ship.modules.length; i++) {
      const m = ship.modules[i];
      if (m.hp <= 0) continue;
      if (!isDockingModule(m)) continue;
      if (m.docking?.peerShipId != null) continue;
      const w = moduleWorldCenter(ship, m);
      _collarCands.push({ ship, module: m, idx: i, cx: w.x, cy: w.y });
    }
  }
}

/** Same result as findNearestOtherCollar, reading the prebuilt candidate list.
 *  Caller must rebuildCollarCands() for the current state first. */
function nearestCachedCollar(selfShip: Ship, selfCollar: ShipModule): DockingPeer | null {
  const sw = moduleWorldCenter(selfShip, selfCollar);
  let best: DockingPeer | null = null;
  let bestD2 = ATTRACT_RANGE_M * ATTRACT_RANGE_M;
  for (let i = 0; i < _collarCands.length; i++) {
    const cand = _collarCands[i];
    if (cand.ship === selfShip) continue;
    const dx = cand.cx - sw.x;
    const dy = cand.cy - sw.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = { ship: cand.ship, module: cand.module, moduleIdx: cand.idx, distance: Math.sqrt(d2) };
    }
  }
  return best;
}

export function findNearestOtherCollar(
  world: World,
  selfShip: Ship,
  selfCollar: ShipModule,
): DockingPeer | null {
  const sw = moduleWorldCenter(selfShip, selfCollar);
  let best: DockingPeer | null = null;
  let bestD2 = ATTRACT_RANGE_M * ATTRACT_RANGE_M;
  for (const other of world.ships.values()) {
    if (other === selfShip || other.isAsteroid) continue;
    for (let i = 0; i < other.modules.length; i++) {
      const m = other.modules[i];
      if (m.hp <= 0) continue;
      if (!isDockingModule(m)) continue;
      if (m.docking?.peerShipId != null) continue;
      const w = moduleWorldCenter(other, m);
      const dx = w.x - sw.x;
      const dy = w.y - sw.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = { ship: other, module: m, moduleIdx: i, distance: Math.sqrt(d2) };
      }
    }
  }
  return best;
}

export function evaluateLockOptions(
  world: World,
  host: Ship,
  hostCollar: ShipModule,
  target: Ship,
  targetCollar: ShipModule,
): { trade: { ok: boolean; reason: string }; board: { ok: boolean; reason: string } } {
  const hostCrew = crewInComponentOf(world, host, hostCollar.gx, hostCollar.gy);
  const targetCrew = crewInComponentOf(world, target, targetCollar.gx, targetCollar.gy);

  let avgFriend = 0;
  if (targetCrew.length > 0) {
    let sum = 0;
    for (const c of targetCrew) sum += c.friendliness;
    avgFriend = sum / targetCrew.length;
  }
  const tradeOk = targetCrew.length === 0 || avgFriend >= TRADE_FRIENDLINESS_THRESHOLD;
  const tradeReason = tradeOk
    ? "ready to trade"
    : `crew hostile (avg friendliness ${avgFriend.toFixed(1)})`;

  const boardOk = targetCrew.length === 0
    ? hostCrew.length > 0
    : hostCrew.length >= BOARD_CONTROL_RATIO * targetCrew.length;
  const boardReason = boardOk
    ? "ready to board"
    : `need ≥${BOARD_CONTROL_RATIO.toFixed(2)}× crew (host ${hostCrew.length}, target ${targetCrew.length})`;
  return {
    trade: { ok: tradeOk, reason: tradeReason },
    board: { ok: boardOk, reason: boardReason },
  };
}

export function engageLock(
  world: World,
  host: Ship,
  hostIdx: number,
  hostCollar: ShipModule,
  target: Ship,
  targetIdx: number,
  targetCollar: ShipModule,
): boolean {
  if (!hostCollar.docking || !targetCollar.docking) return false;
  hostCollar.docking.peerShipId = target.id;
  hostCollar.docking.peerModuleIdx = targetIdx;
  hostCollar.docking.lockedAt = world.tick;
  hostCollar.docking.attract = false;
  targetCollar.docking.peerShipId = host.id;
  targetCollar.docking.peerModuleIdx = hostIdx;
  targetCollar.docking.lockedAt = world.tick;
  targetCollar.docking.attract = false;
  targetCollar.docking.mode = hostCollar.docking.mode;

  if (hostCollar.docking.mode === "trade") {
    host.tradeWithShipId = target.id;
    target.tradeWithShipId = host.id;
  } else {
    target.boardedByShipId = host.id;
    for (const c of world.crew.values()) {
      if (c.shipId !== target.id) continue;
      c.friendliness = Math.max(c.friendliness, 10);
    }
  }
  // Zero relative motion so the kinematic snap doesn't kick.
  target.body.vel.x = host.body.vel.x;
  target.body.vel.y = host.body.vel.y;
  target.body.angVel = host.body.angVel;
  target.throttle = 0;
  target.steer = 0;
  target.strafe = 0;
  target.fire1 = false;
  return true;
}

export function releaseLock(world: World, ship: Ship, collar: ShipModule): boolean {
  const dock = collar.docking;
  if (!dock || dock.peerShipId == null || dock.peerModuleIdx == null) return false;
  const peerShipId = dock.peerShipId;
  const peerShip = world.ships.get(peerShipId);
  if (peerShip) {
    const peerModule = peerShip.modules[dock.peerModuleIdx];
    if (peerModule && peerModule.docking
        && peerModule.docking.peerShipId === ship.id) {
      peerModule.docking.peerShipId = null;
      peerModule.docking.peerModuleIdx = null;
      peerModule.docking.lockedAt = null;
    }
    if (peerShip.boardedByShipId === ship.id) peerShip.boardedByShipId = null;
    if (peerShip.tradeWithShipId === ship.id) peerShip.tradeWithShipId = null;
  }
  if (ship.boardedByShipId === peerShipId) ship.boardedByShipId = null;
  if (ship.tradeWithShipId === peerShipId) ship.tradeWithShipId = null;
  dock.peerShipId = null;
  dock.peerModuleIdx = null;
  dock.lockedAt = null;
  return true;
}

interface CollarAlignment {
  axialCos: number;
  facingCos: number;
  aligned: boolean;
}

export function collarAlignment(
  host: Ship,
  hostCollar: ShipModule,
  peerShip: Ship,
  peerCollar: ShipModule,
): CollarAlignment {
  const a = moduleWorldCenter(host, hostCollar);
  const b = moduleWorldCenter(peerShip, peerCollar);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = Math.hypot(dx, dy);
  const hostDir = collarDockDir(host, hostCollar);
  const peerDir = collarDockDir(peerShip, peerCollar);
  const axialCos = d > 1e-3 ? (dx * hostDir.x + dy * hostDir.y) / d : 1;
  const facingCos = hostDir.x * peerDir.x + hostDir.y * peerDir.y;
  return {
    axialCos,
    facingCos,
    aligned: axialCos >= LOCK_AXIAL_COS && facingCos <= LOCK_FACING_COS,
  };
}

export function isLockable(host: Ship, hostCollar: ShipModule, world: World): DockingPeer | null {
  if (!hostCollar.docking) return null;
  if (hostCollar.docking.peerShipId != null) return null;
  const peer = findNearestOtherCollar(world, host, hostCollar);
  if (!peer) return null;
  if (peer.distance > LOCK_RANGE_M) return null;
  if (!collarAlignment(host, hostCollar, peer.ship, peer.module).aligned) return null;
  return peer;
}

function bondIsValid(world: World, ship: Ship, collar: ShipModule): boolean {
  const d = collar.docking;
  if (!d || d.peerShipId == null || d.peerModuleIdx == null) return false;
  const peerShip = world.ships.get(d.peerShipId);
  if (!peerShip) return false;
  const peerModule = peerShip.modules[d.peerModuleIdx];
  if (!peerModule || peerModule.hp <= 0) return false;
  if (!isDockingModule(peerModule)) return false;
  const pd = peerModule.docking;
  if (!pd) return false;
  if (pd.peerShipId !== ship.id) return false;
  if (pd.peerModuleIdx == null || ship.modules[pd.peerModuleIdx] !== collar) return false;
  return true;
}

export function stepDocking(world: World, dt: number): void {
  rebuildCollarCands(world);
  for (const ship of world.ships.values()) {
    for (const m of ship.modules) {
      if (m.hp <= 0 || m.disabled) continue;
      if (!isDockingModule(m)) continue;
      const dock = m.docking;
      if (!dock || !dock.attract || dock.peerShipId != null) continue;
      const peer = nearestCachedCollar(ship, m);
      if (!peer) continue;
      const a = moduleWorldCenter(ship, m);
      const b = moduleWorldCenter(peer.ship, peer.module);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.hypot(dx, dy);
      if (d < 1e-3) continue;
      const falloff = 1 - Math.min(1, d / ATTRACT_RANGE_M);
      const F = ATTRACT_FORCE_N * falloff;
      const invA = ship.body.mass > 0 ? 1 / ship.body.mass : 0;
      const invB = peer.ship.body.mass > 0 ? 1 / peer.ship.body.mass : 0;
      const nx = dx / d, ny = dy / d;
      const dvx = peer.ship.body.vel.x - ship.body.vel.x;
      const dvy = peer.ship.body.vel.y - ship.body.vel.y;
      const closing = -(dvx * nx + dvy * ny);
      if (closing < ATTRACT_MAX_CLOSING_M_S) {
        ship.body.vel.x += nx * F * invA * dt;
        ship.body.vel.y += ny * F * invA * dt;
        peer.ship.body.vel.x -= nx * F * invB * dt;
        peer.ship.body.vel.y -= ny * F * invB * dt;
      }
    }
  }

  // Lower-id ship is host so the snap is symmetric and idempotent.
  for (const ship of world.ships.values()) {
    for (let i = 0; i < ship.modules.length; i++) {
      const m = ship.modules[i];
      if (m.hp <= 0) continue;
      if (!isDockingModule(m)) continue;
      const dock = m.docking;
      if (!dock || dock.peerShipId == null || dock.peerModuleIdx == null) continue;
      if (!bondIsValid(world, ship, m)) {
        releaseLock(world, ship, m);
        continue;
      }
      const peerShip = world.ships.get(dock.peerShipId);
      if (!peerShip) continue;
      const peerModule = peerShip.modules[dock.peerModuleIdx];
      if (!peerModule) continue;
      if (ship.id > peerShip.id) continue;
      snapPeerToHost(ship, m, peerShip, peerModule);
    }
  }

  // Lock loop above changed bonding/positions; refresh before the flash pass.
  rebuildCollarCands(world);
  const flash = Math.sin(world.time * Math.PI * 2 * FLASH_HZ) > 0;
  for (const ship of world.ships.values()) {
    for (let i = 0; i < ship.modules.length; i++) {
      const m = ship.modules[i];
      if (m.hp <= 0) continue;
      if (!isDockingModule(m)) continue;
      const dock = m.docking;
      if (!dock) continue;

      const a = moduleWorldCenter(ship, m);
      let sprite: string | null = null;
      let to: { x: number; y: number } | null = null;

      if (dock.peerShipId != null && dock.peerModuleIdx != null) {
        if (ship.id > dock.peerShipId) continue;
        const peerShip = world.ships.get(dock.peerShipId);
        if (!peerShip) continue;
        const peerModule = peerShip.modules[dock.peerModuleIdx];
        if (!peerModule) continue;
        to = moduleWorldCenter(peerShip, peerModule);
        sprite = dock.mode === "board" ? "dock_line_orange" : "dock_line_green";
      } else if (dock.attract) {
        const peer = nearestCachedCollar(ship, m);
        if (!peer) continue;
        if (!flash) continue;
        to = moduleWorldCenter(peer.ship, peer.module);
        sprite = "dock_line_red";
      }

      if (sprite && to) {
        world.beams.push({
          fromX: a.x, fromY: a.y,
          toX: to.x, toY: to.y,
          widthM: shipCellSize(ship) * 0.3,
          sprite,
          vx: ship.body.vel.x,
          vy: ship.body.vel.y,
        });
      }
    }
  }
}

function snapPeerToHost(
  host: Ship,
  hostCollar: ShipModule,
  peer: Ship,
  peerCollar: ShipModule,
): void {
  const hostCenter = moduleWorldCenter(host, hostCollar);
  const hostDir = collarDockDir(host, hostCollar);
  // Centre-to-centre gap when docked: half the host collar + half the peer collar.
  const gap = (shipCellSize(host) + shipCellSize(peer)) * 0.5;
  const wantX = hostCenter.x + hostDir.x * gap;
  const wantY = hostCenter.y + hostDir.y * gap;

  const peerFacingAng = peerCollar.facing * (Math.PI / 2);
  const wantPeerAng = jitterAngle(Math.atan2(-hostDir.y, -hostDir.x)) - peerFacingAng;

  peer.body.rot = wantPeerAng;

  const snapRot = jitterAngle(peer.body.rot);
  const cs = Math.cos(snapRot);
  const sn = Math.sin(snapRot);
  const peerCell = shipCellSize(peer);
  const localX = (peerCollar.gx + 0.5) * peerCell - peer.com.x;
  const localY = (peerCollar.gy + 0.5) * peerCell - peer.com.y;
  const worldX = localX * cs - localY * sn;
  const worldY = localX * sn + localY * cs;
  peer.body.pos.x = wantX - worldX;
  peer.body.pos.y = wantY - worldY;

  peer.body.vel.x = host.body.vel.x;
  peer.body.vel.y = host.body.vel.y;
  peer.body.angVel = host.body.angVel;
}
